import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { ConfigService } from 'src/app/services/config/config.service';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { environment } from 'src/environments/non-vendor/environment';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-primary-use-step',
  templateUrl: './primary-use-step.component.html',
  styleUrls: ['./primary-use-step.component.scss']
})
export class PrimaryUseStepComponent implements OnInit {
  title: string = "";
  subtitleBeginning: string = "";
  subtitleMiddle: string = "";
  subtitleEnd: string = "";
  i18nSubtitle: string = "";
  isEnglish: boolean = true;
  isContinueBtnDisabled: boolean = true;
  primaryUseOptions: WizardOption[] = [];
  currentStep: WizardStep = new WizardStep();
  stepNumber: number = 0;
  isSingleIndustry: boolean = false;
  notYourIndustryLinkText: string = "";
  notYourIndustryLinkUrl: string = "";
  analyticsStepName: string = AnalyticsUtility.wizardStepName.primary;
  showFlexLink: boolean = false;
  flexLinkUrl: string = "";

  constructor(
    private analyticsService: AnalyticsService,
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private confSvc: ConfigService,
    private utilityService: UtilityService) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.PrimaryUse);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);

    const primaryUseOptionsToDisplay = this.confSvc.getStringArray("primaryUseOptionsToDisplay");
    this.isSingleIndustry = primaryUseOptionsToDisplay.length <= 1;
    this.notYourIndustryLinkText = this.confSvc.getString("notYourIndustryLinkText");
    this.notYourIndustryLinkUrl = environment.notYourIndustryLinkUrl;

    this.showFlexLink = this.confSvc.getBool("showFlexLink");
    this.flexLinkUrl = this.confSvc.getString("flexLinkUrl");

    this.title = this.isSingleIndustry ? this.confSvc.getString("primaryUseTitleIndustryIdentified") : this.confSvc.getString("primaryUseTitle");
    this.subtitleBeginning = this.confSvc.getString("wizardStepSubtitleBeginning");
    this.subtitleMiddle = this.confSvc.getString("singleSelectionWizardStepSubtitleMiddle");
    this.subtitleEnd = this.confSvc.getString("singleSelectionWizardStepSubtitleEnd");
    this.i18nSubtitle = this.confSvc.getString("i18nSingleSelectionWizardStepSubtitle");

    this.isEnglish = this.utilityService.getLocaleInfo().isEnglish;

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);

      this.setPrimaryUseOptions();
    });
    this.analyticsService.logCurrentPage(this.analyticsStepName);
  }

  setPrimaryUseOptions(): void {
    this.wizardService.getSelectedWizardOptionsForStep(this.currentStep.name)
      .subscribe((selectedOptions: WizardOption[]) => {
        this.isContinueBtnDisabled = selectedOptions.length === 0;

        if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("primaryUseBusinessTitle"), this.confSvc.getStringArray("primaryUseOptionsToDisplay"))) {
          this.primaryUseOptions.push(Object.assign(new WizardOption(), {
            step: this.currentStep,
            title: this.confSvc.getString("primaryUseBusinessTitle"),
            iconFontSet: 'fas',
            iconName: 'Industry_Business.png',
            isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("primaryUseBusinessTitle"))
          }));
        }

        if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("primaryUseEducationTitle"), this.confSvc.getStringArray("primaryUseOptionsToDisplay"))) {
          this.primaryUseOptions.push(Object.assign(new WizardOption(), {
            step: this.currentStep,
            title: this.confSvc.getString("primaryUseEducationTitle"),
            iconFontSet: 'fas',
            iconName: 'Industry_Education.png',
            isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("primaryUseEducationTitle"))
          }));
        }

        if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("primaryUseGovernmentTitle"), this.confSvc.getStringArray("primaryUseOptionsToDisplay"))) {
          this.primaryUseOptions.push(Object.assign(new WizardOption(), {
            step: this.currentStep,
            title: this.confSvc.getString("primaryUseGovernmentTitle"),
            iconFontSet: 'fas',
            iconName: 'Industry_Government.png',
            isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("primaryUseGovernmentTitle"))
          }));
        }

        if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("primaryUseHealthcareTitle"), this.confSvc.getStringArray("primaryUseOptionsToDisplay"))) {
          this.primaryUseOptions.push(Object.assign(new WizardOption(), {
            step: this.currentStep,
            title: this.confSvc.getString("primaryUseHealthcareTitle"),
            iconFontSet: 'fas',
            iconName: 'Industry_Healthcare.png',
            isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("primaryUseHealthcareTitle"))
          }));
        }

        if(this.isSingleIndustry) {
          this.isContinueBtnDisabled = false;
          this.primaryUseOptions[0].isSelected = true;
          this.onSelect(this.primaryUseOptions[0]);
        }

      });
  };

  onSelect(event: WizardOption): void {
    this.wizardService.removeAllSelectedWizardOptions();

    this.isContinueBtnDisabled = !event.isSelected;
    this.primaryUseOptions.map(item => {
      if (item.title !== event.title) {
        item.isSelected = false;
        this.wizardService.removeSelectedWizardOption(item.title);
      } else {
        item.isSelected = event.isSelected;
      }
    });
    if (event.isSelected) {
      this.wizardService.addSelectedWizardOption(event);
    }
  };

  logLinkClick(){
    this.analyticsService.flexLinkClicked(this.stepNumber);
    window.open(this.flexLinkUrl, "_blank");
  };

  onSkip(): void {
    this.primaryUseOptions.forEach((option) => {
      this.wizardService.addSelectedWizardOption(option)
    })
  }
}
